// src/components/Header.js
import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Tooltip } from '@material-ui/core';
import { useAuth } from '../contexts/AuthContext'; 
import Modal from 'react-modal';
import Avatar from './Avatar';
import NotificationsBell from './NotificationsBell';
import { GoogleAuthProvider } from 'firebase/auth'; 
import ZmenaPrezdivky from '../ZmenaPrezdivky';
import { useHistory, Link } from 'react-router-dom';
import HeaderDropdown from '../HeaderDropdown';
import PridatRoli from '../PridatRoli'; // Import komponenty PridatRoli
import SearchPopup from './SearchPopup'; // <-- new import

Modal.setAppElement('#root');

function Header() {
    const { userCount, loadingUserCount } = useAuth(); // Použijeme loadingUserCount
    const { user, login, logout, loading, role } = useAuth();
    const [scrolled, setScrolled] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isNicknameModalOpen, setIsNicknameModalOpen] = useState(false); 
    const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
    const [isAddRoleModalOpen, setIsAddRoleModalOpen] = useState(false); // Stav pro modální okno Přidat roli
    const [isSearchOpen, setIsSearchOpen] = useState(false); // Stav pro hledací popup

    const history = useHistory();
    const userRef = useRef(null);
    const hoverCloseTimeout = useRef(null);

    const openSearch = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        setIsSearchOpen(true);
    };
    const closeSearch = () => setIsSearchOpen(false);

    useEffect(() => {
        const getWindowScroll = () =>
            (typeof window !== 'undefined' ? (window.pageYOffset || window.scrollY || 0) : 0)
            || (document.documentElement && document.documentElement.scrollTop) 
            || (document.body && document.body.scrollTop)
            || (document.scrollingElement && document.scrollingElement.scrollTop)
            || 0;

        const measureAll = () => {
            try {
                const candidates = [
                    document.querySelector('[data-scroll-root]'),
                    document.querySelector('main'),
                    document.getElementById('root'),
                    document.scrollingElement,
                    document.documentElement,
                    document.body,
                ].filter(Boolean);
                let maxPos = getWindowScroll();
                for (const el of candidates) {
                    try {
                        const st = el.scrollTop || 0;
                        if (st > maxPos) maxPos = st;
                    } catch {}
                }
                return maxPos;
            } catch {
                return getWindowScroll();
            }
        };

        const handleScroll = () => {
            const pos = measureAll();
            setScrolled(pos > 2);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        document.addEventListener('scroll', handleScroll, { passive: true });
        const mainEl = document.querySelector('main');
        if (mainEl) mainEl.addEventListener('scroll', handleScroll, { passive: true });

        // Safety net: observe via animation frame in case some scrollable container doesn't emit listeners here
        let rafId = 0;
        let last = -1;
        const loop = () => {
            try {
                const pos = measureAll();
                if (pos !== last) {
                    last = pos;
                    setScrolled(pos > 2);
                }
            } catch {}
            rafId = window.requestAnimationFrame(loop);
        };
        rafId = window.requestAnimationFrame(loop);

        handleScroll();

        return () => {
            window.removeEventListener('scroll', handleScroll);
            document.removeEventListener('scroll', handleScroll);
            if (mainEl) mainEl.removeEventListener('scroll', handleScroll);
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, []);

    const openModal = () => setIsModalOpen(true);
    const closeModal = () => setIsModalOpen(false);
    const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);

    // Hover handlers: open on enter, close shortly after leave (avoid flicker)
    // Ignore events that originate from the notifications bell (we don't want bell hover to open dropdown)
    const handleUserMouseEnter = (e) => {
        try {
            if (e && e.target && e.target.closest && e.target.closest('.hdr-bell')) return;
        } catch {}
        if (hoverCloseTimeout.current) {
            clearTimeout(hoverCloseTimeout.current);
            hoverCloseTimeout.current = null;
        }
        setIsDropdownOpen(true);
    };

    const handleUserMouseLeave = (e) => {
        // If we're moving to an element inside the user section (including bell), don't close
        try {
            if (e && e.relatedTarget && userRef.current && userRef.current.contains(e.relatedTarget)) return;
        } catch {}
        if (hoverCloseTimeout.current) clearTimeout(hoverCloseTimeout.current);
        hoverCloseTimeout.current = setTimeout(() => {
            setIsDropdownOpen(false);
            hoverCloseTimeout.current = null;
        }, 200);
    };
    // When the pointer moves inside the user section, open the dropdown
    // once the pointer leaves the bell area (.hdr-bell) and moves over other parts
    const handleUserMouseMove = (e) => {
        try {
            if (isDropdownOpen) return;
            const overBell = e && e.target && e.target.closest && e.target.closest('.hdr-bell');
            // if pointer isn't over the bell, open the dropdown
            if (!overBell) {
                if (hoverCloseTimeout.current) {
                    clearTimeout(hoverCloseTimeout.current);
                    hoverCloseTimeout.current = null;
                }
                setIsDropdownOpen(true);
            }
        } catch {}
    };
    const openNicknameModal = () => setIsNicknameModalOpen(true);
    const closeNicknameModal = () => setIsNicknameModalOpen(false);
    const openAlertModal = () => setIsAlertModalOpen(true);
    const closeAlertModal = () => setIsAlertModalOpen(false);
    const openAddRoleModal = () => setIsAddRoleModalOpen(true); // Funkce pro otevření modálního okna Přidat roli
    const closeAddRoleModal = () => setIsAddRoleModalOpen(false); // Funkce pro zavření modálního okna Přidat roli

    const confirmLogout = async () => {
        try {
            await logout();
            closeModal();
        } catch (error) {
            console.error('Error during logout:', error);
        }
    };

    const handleNicknameChange = () => {
        if (role === 'VIP' || role === 'Majitel' || role === 'Admin' || role === 'Moderátor') {
            openNicknameModal();
        } else {
            openAlertModal();
        }
    };

    const navigateToProfile = () => {
        history.push('/profil');
        setIsDropdownOpen(false);
    };

    const addRole = () => {
        openAddRoleModal(); // Otevře modální okno Přidat roli
    };

    // Close dropdown when clicking outside of user area
    useEffect(() => {
        const onDocClick = (e) => {
            if (!isDropdownOpen) return;
            try {
                if (userRef.current && !userRef.current.contains(e.target)) {
                    setIsDropdownOpen(false);
                }
            } catch (err) {
                // ignore
            }
        };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('touchstart', onDocClick);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('touchstart', onDocClick);
        };
    }, [isDropdownOpen]);

    const getRoleColor = (role) => {
        switch (role) {
            case 'Majitel':
                return '#f39c12'; // Žlutá
            case 'Admin':
                return '#e74c3c'; // Červená
            case 'Moderátor':
                return '#2ecc71'; // Světle zelená
            case 'VIP':
                return '#e67e22'; // Světle oranžová
            default:
                return '#3498db'; // Modrá
        }
    };

    const allowedRoles = ['Majitel', 'Admin', 'Moderátor', 'VIP'];

    const shouldShowVIPLabel = !['VIP', 'Majitel', 'Admin', 'Moderátor'].includes(role);

    return (
        <>
            <Nav scrolled={scrolled}>
                <Logo>
                    <Tooltip title="Verze 2.0" placement="bottom-start" arrow enterDelay={100} leaveDelay={50}>
                        <Link to="/">
                            <div className="logonceheader">
                                <img src="https://i.ibb.co/qrpqqR0/NCELOGOFINA.png" alt="NCE+" loading="eager" />
                            </div>
                        </Link>
                    </Tooltip>
                </Logo>
                <NavMenu>
                    <Link className='cursorpointer' to="/">
                        <img className="prevent-select" src="/images/horni-menu/DOMŮ1.png" alt="DOMŮ" loading="eager" />
                        <span>DOMŮ</span>
                    </Link>
                    <a className='cursorpointer' href="#" onClick={openSearch}>
                        <img className="prevent-select" src="/images/horni-menu/HLEDAT.png" alt="HLEDAT" loading="eager" />
                        <span>HLEDAT</span>
                    </a>
                    <Link className='cursorpointer' to="/serialy">
                        <img className="prevent-select" src="/images/horni-menu/SERIALY1.png" alt="SERIÁLY" loading="eager" />
                        <span>SERIÁLY</span>
                    </Link>
                    <Link className='cursorpointer' to="/filmy">
                        <img className="prevent-select" src="/images/horni-menu/FILMY1.png" alt="FILMY" loading="eager" />
                        <span>FILMY</span>
                    </Link>
                                        {user && (
                                            <Link className='cursorpointer' to="/Watchlist">
                                                    <img className="prevent-select" src="/images/horni-menu/WATCHLIST.png" alt="WATCHLIST" loading="eager" />
                                                    <span>WATCHLIST</span>
                                            </Link>
                                        )}
                    {allowedRoles.includes(role) ? (
                       <Link className='cursorpointer' to="/vip-obsah">
                       <img className="prevent-select" src="/images/horni-menu/VIPOBSAH.png" alt="VIP OBSAH" loading="eager" />
                       <span>VIP OBSAH</span>
                   </Link>
                    ) : null}      
                    <Link className='cursorpointer' to="/od-nce+">
                        <img className="prevent-select" src="/images/horni-menu/OD_NCE+1.png" alt="OD NCE+" loading="eager" />
                        <span>OD NCE+</span>
                    </Link>      
                </NavMenu>
                <AuthSection>
                    {loading ? (
                        <Spinner>Načítám...</Spinner>
                    ) : user ? (
                        <UserSection ref={userRef} onMouseEnter={handleUserMouseEnter} onMouseLeave={handleUserMouseLeave} onMouseMove={handleUserMouseMove}>
                            <div className="hdr-bell"><NotificationsBell /></div>
                            <Avatar key={user.photoURL} user={user} />
                            <UserName onClick={toggleDropdown}>
                                {user.displayName || 'Neznámý'}
                                {role && <RoleBadge color={getRoleColor(role)}>{role === 'User' ? 'Uživatel' : role}</RoleBadge>}
                            </UserName>
                            <HeaderDropdown 
                                isDropdownOpen={isDropdownOpen}
                                navigateToProfile={navigateToProfile}
                                handleNicknameChange={handleNicknameChange}
                                openModal={openModal}
                                shouldShowVIPLabel={shouldShowVIPLabel}
                                userRole={role}
                                openAddRoleModal={addRole} // Propojení s funkcí pro otevření modálního okna Přidat roli
                            />
                        </UserSection>
                    ) : (
                        <LoginButton onClick={() => login(new GoogleAuthProvider())}>Přihlásit se</LoginButton>
                    )}
                </AuthSection>
            </Nav>

            <SearchPopup isOpen={isSearchOpen} onClose={closeSearch} role={role} />

            <Modal
                isOpen={isModalOpen}
                onRequestClose={closeModal}
                contentLabel="Confirm Logout"
                style={{
                    overlay: modalStyles.overlay,
                    content: { ...modalStyles.content, background: 'transparent', padding: '0', border: 'none', boxShadow: 'none' }
                }}
            >
                <PopupCard>
                    <PopupTitle>Chcete se opravdu odhlásit?</PopupTitle>
                    <PopupButtons>
                        <PopupButton onClick={confirmLogout}>Ano</PopupButton>
                        <PopupButton variant="secondary" onClick={closeModal}>Ne</PopupButton>
                    </PopupButtons>
                </PopupCard>
            </Modal>

            <Modal
                isOpen={isNicknameModalOpen}
                onRequestClose={closeNicknameModal}
                contentLabel="Změna přezdívky"
                // make react-modal content transparent so the inner ZmenaPrezdivky card renders without an extra grey shell
                style={{
                    overlay: modalStyles.overlay,
                    content: { ...modalStyles.content, background: 'transparent', padding: '0', border: 'none', boxShadow: 'none' }
                }}
            >
                <ZmenaPrezdivky closeModal={closeNicknameModal} />
            </Modal>

            <Modal
                isOpen={isAlertModalOpen}
                onRequestClose={closeAlertModal}
                contentLabel="Upozornění"
                style={modalStyles}
            >
                <ModalContent>
                    <ModalTitle>Pro změnu přezdívky musíte mít VIP roli</ModalTitle>
                    <ModalButtons>
                        <ModalButton onClick={closeAlertModal}>OK</ModalButton>
                    </ModalButtons>
                </ModalContent>
            </Modal>

            <Modal
                isOpen={isAddRoleModalOpen}
                onRequestClose={closeAddRoleModal}
                contentLabel="Přidat roli"
                style={modalStyles}
            >
                <PridatRoli closeModal={closeAddRoleModal} />
            </Modal>
        </>
    );
}

const modalStyles = {
    overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        zIndex: 1000,
    },
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        borderRadius: '8px',
        padding: '20px',
        border: 'none',
        background: '#333',
        color: '#fff',
        zIndex: 1000,
    }
};

const Nav = styled.nav`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 24px;
    background: ${props => props.scrolled ? 'rgba(9,11,19,0.92)' : 'transparent'};
    box-shadow: ${props => props.scrolled ? '0 6px 24px rgba(0,0,0,0.5)' : 'none'};
    -webkit-backdrop-filter: ${props => props.scrolled ? 'saturate(120%) blur(6px)' : 'none'};
    backdrop-filter: ${props => props.scrolled ? 'saturate(120%) blur(6px)' : 'none'};
    transition: background 240ms ease, box-shadow 240ms ease, backdrop-filter 240ms ease;
    will-change: background, box-shadow, backdrop-filter;
`;

const Logo = styled.div`
    width: 90px;
    margin-top: 4px;
    max-height: 70px;
    display: inline-block;
    cursor: pointer;

    img {
        display: block;
        width: 100%;
    }

          @media (max-width: 768px) {
               width: 25px;

                   img {
        display: block;
        width: 120%;
    }
}
}
`;

const NavMenu = styled.div`
    display: flex;
    align-items: center;
    height: 100%;
    margin-left: 30px;
    justify-content: flex-start;

    a {
        text-decoration: none;
        display: flex;
        align-items: center;
        padding: 0 15px;

        img {
            height: 22px;
            margin-bottom: 1px;
        }

        span {
            font-family: 'Noto Sans Lao', sans-serif;
            color: rgb(249, 249, 249);
            font-size: 13px;
            letter-spacing: 1.42px;
            line-height: 1.08;
            padding: 2px 5px;
            white-space: nowrap;
            position: relative;

            &:before {
                background-color: rgb(249, 249, 249);
                border-radius: 0px 0px 4px 4px;
                bottom: -6px;
                content: "";
                height: 2px;
                left: 0px;
                opacity: 0;
                position: absolute;
                right: 0px;
                transform-origin: left center;
                transform: scaleX(0);
                transition: all 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 0s;
                visibility: hidden;
                width: auto;
            }
        }

        &:hover {
            span:before {
                transform: scaleX(1);
                visibility: visible;
                opacity: 1 !important;
            }
        }
      @media (max-width: 768px) {
           padding: 0;
    span { 
      font-size: 0px;
     }
              img {
            height: 32px;
            margin-bottom: 1px;
        }
                &:hover {
            span:before {
                transform: scaleX(0);
                visibility: hidden;
                opacity: 0 !important;
            }
    }
`;

const AuthSection = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-left: auto;
`;



const UserSection = styled.div`
    display: flex;
    align-items: center;
    position: relative;
    cursor: pointer;

              @media (max-width: 768px) {
    font-size: 0px;
    margin-bottom -2px;
}
`;

const UserName = styled.div`
    margin-left: 10px;
    color: #fff;
    font-size: 14px;
    cursor: pointer;
    display: flex;
    font-weight: 500;
    align-items: center;

          @media (max-width: 768px) {
    font-size: 0px;
}
`;

const RoleBadge = styled.div`
    background: ${props => props.color || '#444'};
    color: #fff;
    border-radius: 12px;
    font-weight: normal;
    padding: 2px 8px;
    font-size: 12px;
    margin-left: 8px;
`;

const LoginButton = styled.button`
    background-color: #4285f4;
    border: none;
    color: white;
    padding: 10px 20px;
    font-size: 14px;
    border-radius: 5px;
    cursor: pointer;
    transition: background-color 0.3s ease;

    &:hover {
        background-color: #357ae8;
    }

    @media (max-width: 768px) {
        padding: 10px 8px;
          font-size: 12px;
          margin-bottom: -4px;
          margin-left 10px;
    }
}
`;

const Spinner = styled.div`
    color: #fff;
`;

const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const ModalTitle = styled.h2`
    margin-bottom: 20px;
`;

const ModalButtons = styled.div`
    display: flex;
    gap: 10px;
`;

const ModalButton = styled.button`
    background: #e50914;
    border: none;
    color: #fff;
    padding: 10px 20px;
    border-radius: 5px;
    cursor: pointer;
    transition: background-color 0.3s ease;

    &:hover {
        background: #f40612;
    }
`;

// Styled popup card used for consistent admin/user popups (matches site style)
const PopupCard = styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    width: 520px;
    max-width: calc(100vw - 48px);
    padding: 18px 20px;
    border-radius: 12px;
    box-sizing: border-box;
    background: #071014;
    color: #fff;
    border: 1px solid rgba(255,255,255,0.04);
    box-shadow: 0 20px 60px rgba(0,0,0,0.6);
`;

const PopupTitle = styled.h2`
    margin: 6px 0 12px 0;
    font-size: 20px;
    text-align: left;
    font-weight: 700;
`;

const PopupButtons = styled.div`
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 18px;
`;

const PopupButton = styled.button`
    background: ${props => props.variant === 'secondary' ? '#2b2f33' : '#22c55e'};
    border: none;
    color: #fff;
    padding: 10px 18px;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 700;
    transition: transform .12s ease, filter .12s ease;
    &:hover { filter: brightness(1.06); transform: translateY(-2px); }
    &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; filter: none; }
`;

export default Header;
